import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExternalLink,
  Github,
  Sparkles,
  LayoutGrid,
  Smartphone,
  Globe,
} from "lucide-react";

// --- ENHANCED DATA WITH CATEGORIES ---
import projectsData from "../data/projects.json";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Dynamically extract unique categories
const categories = ["All", ...new Set(projectsData.map((p) => p.category))];

// Icons map for categories
const CategoryIcon = ({ category }) => {
  switch (category) {
    case "All":
      return <LayoutGrid className="w-4 h-4" />;
    case "Full Stack":
      return <Globe className="w-4 h-4" />;
    case "Frontend":
      return <Sparkles className="w-4 h-4" />;
    case "Mobile App":
      return <Smartphone className="w-4 h-4" />;
    default:
      return <LayoutGrid className="w-4 h-4" />;
  }
};

const ProjectGridCard = ({ project }) => {
  return (
    <motion.div
      layout // This handles the smooth resort animation!
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
      className="group relative w-full h-full flex flex-col rounded-3xl bg-[#0a0f1d] border border-white/5 overflow-hidden transition-all duration-500 hover:-translate-y-2"
      style={{
        boxShadow: `0 0 0px ${project.colorGlow}`, // Default
      }}
      // Hack to apply dynamic box shadow on hover via Framer Motion
      whileHover={{
        boxShadow: `0 20px 40px ${project.colorGlow.replace("0.4", "0.15")}`,
      }}
    >
      {/* CARD IMAGE HEADER */}
      <div className="relative w-full aspect-video overflow-hidden bg-[#020617]">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d] via-transparent to-transparent z-10" />

        {/* Status Badge */}
        <div className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-xs font-medium text-slate-200 flex items-center gap-2">
          {project.status}
        </div>

        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {/* Faint Background Keyword overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.05] pointer-events-none z-0">
          <span className="text-6xl font-black tracking-tighter uppercase text-white">
            {project.bgKeyword}
          </span>
        </div>
      </div>

      {/* CARD CONTENT */}
      <div className="relative flex flex-col flex-grow p-6 z-20 bg-[#0a0f1d]">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-bold tracking-widest text-purple-400 uppercase mb-2">
              {project.type}
            </p>
            <h3 className="text-2xl font-bold text-white group-hover:text-purple-300 transition-colors">
              {project.title}
            </h3>
          </div>
        </div>

        <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
          {project.desc}
        </p>

        {/* Tech Stack Pills */}
        <div className="flex flex-wrap gap-2 mb-8 mt-auto">
          {project.stack.slice(0, 4).map((tech, i) => (
            <span
              key={i}
              className="px-2.5 py-1 text-[11px] font-medium text-slate-300 bg-white/5 border border-white/10 rounded-md"
            >
              {tech}
            </span>
          ))}
          {project.stack.length > 4 && (
            <span className="px-2.5 py-1 text-[11px] font-medium text-slate-500 bg-white/5 border border-white/10 rounded-md">
              +{project.stack.length - 4}
            </span>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center gap-4 pt-4 border-t border-white/5">
          <a
            href={project.live}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-600/10 text-purple-400 font-medium text-sm hover:bg-purple-600 hover:text-white transition-all duration-300 border border-purple-500/20 hover:border-purple-500"
          >
            <ExternalLink className="w-4 h-4" /> Live Demo
          </a>

          <a
            href={project.github || undefined}
            target={project.github ? "_blank" : undefined}
            rel={project.github ? "noopener noreferrer" : undefined}
            aria-disabled={!project.github}
            onClick={!project.github ? (e) => e.preventDefault() : undefined}
            className={`w-10 h-10 flex flex-shrink-0 items-center justify-center rounded-xl border transition-all duration-300 ${
              project.github
                ? "bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white border-white/10 cursor-pointer"
                : "bg-white/[0.02] text-slate-600 border-white/5 opacity-40 cursor-not-allowed grayscale"
            }`}
            title={project.github ? "View Source" : "GitHub source unavailable"}
          >
            <FontAwesomeIcon icon={faGithub} className="w-4 h-4" fixedWidth />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const [activeTab, setActiveTab] = useState("All");

  // Filter projects based on selected tab
  const filteredProjects = projectsData.filter((project) =>
    activeTab === "All" ? true : project.category === activeTab,
  );

  return (
    <section
      id="all-projects"
      className="relative min-h-screen bg-[#020617] text-slate-200 py-32 overflow-hidden font-sans"
    >
      {/* Background Grid & Glow */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[30vw] bg-purple-600/15 rounded-full blur-[150px] opacity-60 pointer-events-none"></div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="h-px w-10 bg-purple-500"></div>
            <span className="text-purple-400 font-bold tracking-widest uppercase text-xs sm:text-sm">
              Complete Portfolio
            </span>
            <div className="h-px w-10 bg-purple-500"></div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-6xl md:text-7xl font-extrabold text-white mb-6 tracking-tight"
          >
            All{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
              Projects
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-400 text-base md:text-xl max-w-2xl font-light"
          >
            A comprehensive list of products, applications, and experiments I've
            built. Filter by category to explore specific technologies.
          </motion.p>
        </div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap items-center justify-center gap-3 mb-16"
        >
          {categories.map((category) => {
            const isActive = activeTab === category;
            return (
              <button
                key={category}
                onClick={() => setActiveTab(category)}
                className={`relative flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 overflow-hidden ${
                  isActive
                    ? "text-white shadow-[0_0_20px_rgba(147,51,234,0.3)]"
                    : "text-slate-400 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-slate-200"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="active-tab-bg"
                    className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 -z-10"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                {/* Fallback border for active state since bg is absolute */}
                {isActive && (
                  <div className="absolute inset-0 border border-purple-400/50 rounded-full -z-10"></div>
                )}

                <CategoryIcon category={category} />
                {category}
              </button>
            );
          })}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <ProjectGridCard key={project.id} project={project} />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Empty State Fallback (Just in case) */}
        {filteredProjects.length === 0 && (
          <div className="w-full py-32 flex flex-col items-center justify-center text-slate-500">
            <LayoutGrid className="w-16 h-16 mb-4 opacity-20" />
            <p className="text-xl">No projects found in this category.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
